import {
  ArrowLeft,
  Bell,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  FileText,
  Layers3,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { getCourse, getCurriculumEntries, getStudentCourseSignups, getTenant } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { groupCurriculumEntries } from '../../utils/curriculum';
import { getCourseTypeLabel } from '../../utils/courseTypes';
import defaultCourseImage from '../../assets/newimage29-optimized.webp';
import { StudentSidebar } from './StudentSidebar';

const TABS = [
  { value: 'overview', label: 'Overview', icon: BookOpen },
  { value: 'scheme', label: 'Schemes of work', icon: FileText },
  { value: 'assessments', label: 'Assessments', icon: ClipboardCheck },
  { value: 'performance', label: 'Course performance', icon: Sparkles },
  { value: 'profile', label: 'Learning profile', icon: UserRound },
];

function enrollmentProgress(enrollment) {
  const progress = Number(enrollment?.progressPercentage ?? enrollment?.progress ?? 0);
  return Number.isFinite(progress) ? Math.min(100, Math.max(0, progress)) : 0;
}

export function StudentCourseDetailPage() {
  const { tenantId, courseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading, logout } = useAuth();
  const { notify } = useToast();
  const [course, setCourse] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [scheme, setScheme] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeTab = TABS.some((tab) => tab.value === searchParams.get('tab')) ? searchParams.get('tab') : 'overview';

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!tenantId || !courseId || !user?.uid) {
        if (mounted) setLoading(false);
        return;
      }
      try {
        const [initialCourse, signups] = await Promise.all([
          getCourse(tenantId, courseId).catch(() => null),
          getStudentCourseSignups(user.uid),
        ]);
        const matchedEnrollment = signups.find((signup) => signup.courseId === courseId && (!signup.tenantId || signup.tenantId === tenantId))
          || signups.find((signup) => signup.courseId === courseId);
        let nextCourse = initialCourse;
        let resolvedTenantId = tenantId;
        if (!nextCourse && matchedEnrollment?.tenantId) {
          resolvedTenantId = matchedEnrollment.tenantId;
          nextCourse = await getCourse(resolvedTenantId, courseId).catch(() => null);
        }
        const [nextTenant, nextScheme] = await Promise.all([
          getTenant(resolvedTenantId),
          nextCourse ? getCurriculumEntries(resolvedTenantId, courseId).catch(() => []) : Promise.resolve([]),
        ]);
        if (mounted) {
          setCourse(nextCourse);
          setTenant(nextTenant);
          setEnrollment(matchedEnrollment || null);
          setScheme(groupCurriculumEntries(nextScheme));
          setLoading(false);
        }
      } catch (error) {
        console.error('Unable to load student course', error);
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [courseId, tenantId, user?.uid]);

  async function onLogout() {
    await logout();
    notify('You have been signed out.');
  }

  if (authLoading) return <div className="student-course-page-loading">Loading student portal...</div>;
  if (!user) return <Navigate to="/student/login" replace />;

  const selectTab = (tab) => setSearchParams(tab === 'overview' ? {} : { tab });
  const progress = enrollmentProgress(enrollment);
  const firstName = String(user.displayName || user.email || 'Student').split(/[ @]/)[0];
  const currentDate = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date());
  const academyName = tenant?.name || 'Student workspace';

  return (
    <main className="student-portal student-course-view-page">
      <StudentSidebar academyName={academyName} onLogout={onLogout} />

      <div className="student-course-view-shell">
        <section className="student-course-view-workspace">
          <header className="student-course-view-header">
            <div>
              <Link to="/student/courses"><ArrowLeft size={14} />My courses</Link>
              <span>Welcome back, {firstName}</span>
              <p>{currentDate}</p>
            </div>
            <button type="button" className="student-dashboard-notification" aria-label="Notifications">
              <Bell size={19} strokeWidth={1.7} />
              <i />
            </button>
          </header>

          {loading ? (
            <div className="student-course-view-loading"><span /><p>Loading your course...</p></div>
          ) : !course ? (
            <div className="student-course-view-missing">
              <BookOpen size={27} />
              <h1>Course not found</h1>
              <p>This course may no longer be available to this student account.</p>
              <Link to="/student/courses">Back to my courses</Link>
            </div>
          ) : (
            <>
              <section className="student-course-view-hero">
                <img src={course.imageURL || course.imageUrl || defaultCourseImage} alt="" />
                <div>
                  <span>{tenant?.name || 'My learning space'}</span>
                  <h1>{course.name}</h1>
                  <p>{course.description || 'Your connected course learning space.'}</p>
                  <div><b>{getCourseTypeLabel(course.courseType)}</b><b>{course.duration || 'Flexible duration'}</b><b>{course.code || 'Course'}</b></div>
                </div>
              </section>

              <nav className="student-course-view-tabs" aria-label="Course sections">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  return <button type="button" className={activeTab === tab.value ? 'is-active' : ''} onClick={() => selectTab(tab.value)} key={tab.value}><Icon size={14} />{tab.label}</button>;
                })}
              </nav>

              <section className="student-course-view-content">
                {activeTab === 'overview' ? (
                  <div className="student-course-overview-new">
                    <div className="student-course-view-section-heading"><div><h2>Course overview</h2><p>Everything you need to understand your learning journey.</p></div><span>In progress</span></div>
                    <div className="student-course-overview-intro"><span><BookOpen size={18} /></span><div><small>About this course</small><h3>Welcome to {course.name}</h3><p>{course.description || 'Your course materials, live sessions and progress will appear here as your academy publishes them.'}</p></div></div>
                    <div className="student-course-overview-facts">
                      <article><span className="is-violet"><Layers3 size={18} /></span><div><small>Course type</small><strong>{getCourseTypeLabel(course.courseType)}</strong></div></article>
                      <article><span className="is-blue"><Clock3 size={18} /></span><div><small>Duration</small><strong>{course.duration || 'Flexible'}</strong></div></article>
                      <article><span className="is-red"><FileText size={18} /></span><div><small>Learning plan</small><strong>{scheme.length ? `${scheme.length} planned week${scheme.length === 1 ? '' : 's'}` : 'Coming soon'}</strong></div></article>
                    </div>
                  </div>
                ) : activeTab === 'scheme' ? (
                  <div className="student-course-scheme-new">
                    <div className="student-course-view-section-heading"><div><h2>Scheme of work</h2><p>Your course learning path, organised week by week.</p></div><span>{scheme.length} week{scheme.length === 1 ? '' : 's'}</span></div>
                    {scheme.length ? <div className="student-course-scheme-list-new">{scheme.map((week, index) => <article key={week.weekLabel}><span>{String(index + 1).padStart(2, '0')}</span><div><small>{week.weekLabel}</small><h3>{week.weeklyObjective || 'Learning activities'}</h3>{week.entries.map((entry) => <p key={`${entry.dayLabel}-${entry.topic}`}><b>{entry.dayName || entry.dayLabel}</b>{entry.topic || entry.objective || 'Scheduled learning activity'}</p>)}</div></article>)}</div> : <div className="student-course-tab-empty"><FileText size={23} /><h3>Scheme of work coming soon</h3><p>Your academy has not published the learning plan for this course yet.</p></div>}
                  </div>
                ) : (
                  <div className="student-course-tab-empty student-course-tab-empty--large">
                    <Sparkles size={24} />
                    <small>{TABS.find((tab) => tab.value === activeTab)?.label}</small>
                    <h2>Your learning story will appear here.</h2>
                    <p>As your academy adds assessments and activity, this space will show your results, progress and personalised learning insights.</p>
                  </div>
                )}
              </section>
            </>
          )}
        </section>

        <aside className="student-course-view-overview">
          <div className="student-course-view-overview-heading"><h2>Course Progress</h2><CheckCircle2 size={17} /></div>
          <div className="student-course-view-progress" style={{ '--course-view-progress': `${progress * 3.6}deg` }}><div><strong>{progress}%</strong><small>Completed</small></div></div>
          <div className="student-course-view-progress-bar"><div><span>Overall progress</span><strong>{progress}%</strong></div><i><span style={{ width: `${progress}%` }} /></i></div>
          <div className="student-course-view-summary-list">
            <div><span className="is-violet"><BookOpen size={16} /></span><div><small>Course status</small><strong>{enrollment?.status ? enrollment.status.replace(/_/g, ' ') : 'Enrolled'}</strong></div></div>
            <div><span className="is-blue"><CalendarDays size={16} /></span><div><small>Learning plan</small><strong>{scheme.length} week{scheme.length === 1 ? '' : 's'}</strong></div></div>
            <div><span className="is-red"><ClipboardCheck size={16} /></span><div><small>Assessments</small><strong>Coming soon</strong></div></div>
          </div>
          <div className="student-course-view-note"><span><Sparkles size={16} /></span><small>Learning tip</small><h3>Consistency beats intensity.</h3><p>Keep returning to your course, even when you only have a few minutes to learn.</p></div>
          <Link className="student-course-view-classes-link" to="/student/classes">View course classes<ChevronRight size={16} /></Link>
        </aside>
      </div>
    </main>
  );
}
